/**
 * WineCard.js — Hestan Vineyards wine listing card.
 *
 * OVERRIDES: src/components/base/WineCard.js
 *
 * MIGRATION PARITY (old createWineListing, script.js 1871-1923):
 *   - Image at top, clickable → detail view (in-stock only).
 *   - Pill-shaped "Tasting notes" button (calendar icon + outlined border)
 *     above the title (in-stock only).
 *   - Title (bold, single dark color).
 *   - Bottom row: price + size stacked on the LEFT, "BUY NOW" button on the
 *     RIGHT. The button sizes to its content — NOT full-width.
 *   - No Appellation / Varietal / Alcohol meta lines (the old card showed
 *     only the essentials).
 *   - SOLD OUT: bottom-left burgundy overlay on the image, no tasting-notes
 *     button, no CTA. Whole card is clickable → detail view.
 *
 * Broken cover_image_link (eCellar 404): swap to theme logo once via onerror
 * so a missing product photo never leaves an empty/broken card image.
 *
 * INTERFACE: render(wine) → HTMLElement, same signature as base.
 *
 * Reference: hestanvineyards/script.js Lines 1871-1923 (createWineListing).
 */

import { emit ,isChatDisabled} from "../../../core/state.js";
import { getTheme } from "../../../core/configAccessors.js";
import { ASSETS_BASE_URL } from "../../../core/env.js";
import { bindWineDrawerOpen } from "../../../utils/openWineDrawer.js";

const CAL_ICON =
  '<svg viewBox="0 0 13 15" width="13" height="14" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3.83 0.5v2.67M9.17 0.5v2.67M0.5 5.83h12M1.83 1.83h9.34A1.33 1.33 0 0 1 12.5 3.17v9.33a1.33 1.33 0 0 1-1.33 1.33H1.83A1.33 1.33 0 0 1 0.5 12.5V3.17a1.33 1.33 0 0 1 1.33-1.34z"/></svg>';

function isTrue(v) {
  return v === true || v === "true" || v === "True";
}

/** Winery logo when cover_image_link is missing or 404s (eCellar CDN). */
function resolveFallbackSrc() {
  const logo =
    getTheme("card_logo_url") ||
    getTheme("header_icon_url") ||
    getTheme("bot_avatar_url") ||
    null;
  if (!logo) return null;
  if (/^https?:\/\//i.test(logo)) return logo;
  return `${ASSETS_BASE_URL}${logo}`;
}

function bindCoverFallback(img) {
  const fallback = resolveFallbackSrc();
  if (!fallback) return;
  img.addEventListener("error", () => {
    if (img.dataset.wsFallback === "1") return;
    img.dataset.wsFallback = "1";
    img.src = fallback;
    img.classList.add("ws-hestan-cover-fallback");
  });
}

function metaLine(wine) {
  const notes = wine.winemaker_notes || {};
  return [notes.Vintage, notes.Alcohol || notes.alcohol || "", wine.wine_type || ""]
    .filter(Boolean)
    .join(" | ");
}
export default class WineCard {
  render(wine) {
    const card = document.createElement("div");
    card.className = "ws-card ws-wine-card ws-hestan-winecard";
    card.setAttribute("data-variant-id", wine.variant_id ?? wine["Variant SKU"] ?? "");
    card.setAttribute("data-cta-link", wine.cta_link);
    if (wine.unique_id != null) card.dataset.uniqueId = String(wine.unique_id);
    if (wine.wine_key) card.dataset.wineKey = String(wine.wine_key);

    const soldOut = isTrue(wine.sold_out);
    const unavailable = wine.available !== undefined && !isTrue(wine.available);
    const detailLabel = wine.message || `Tell me more about ${wine.title || "this wine"}`;
   const openDetail = () => {

  if (isChatDisabled()) return;

  emit("ws-prompt-select", {
    label: detailLabel,
    value: detailLabel,
    uniqueId: wine.unique_id,
  });
};

    // ── head: image + sold-out overlay ──
    const head = document.createElement("div");
    head.className = "ws-card-head ws-hestan-head";

     // Tasting-notes pill (in-stock only).
      const tasting = document.createElement("button");
      tasting.type = "button";
      tasting.className = "ws-hestan-tasting";
      tasting.innerHTML = `${CAL_ICON}<span>Tasting notes</span>`;
      bindWineDrawerOpen(tasting, wine, card);
      // body.appendChild(tasting);

    const cover = String(wine.cover_image_link || "").trim();
    const fallbackSrc = resolveFallbackSrc();
    const imgSrc = cover || fallbackSrc;
    if (imgSrc) {
      const img = document.createElement("img");
      img.className = "ws-card-img";
      img.src = imgSrc;
      img.alt = wine.title || "Wine";
      img.loading = "lazy";
      if (cover) bindCoverFallback(img);
      else img.classList.add("ws-hestan-cover-fallback");
      if (!soldOut) {
        img.style.cursor = "pointer";
        img.addEventListener("click", openDetail);
      }
      head.appendChild(img);
      head.appendChild(tasting);
    }
    if (soldOut) {
      const overlay = document.createElement("span");
      overlay.className = "ws-hestan-soldout";
      overlay.textContent = "SOLD OUT";
      head.appendChild(overlay);
    }
    card.appendChild(head);

    // ── body ──
    const body = document.createElement("div");
    body.className = "ws-card-body ws-hestan-body";



    if (wine.title) {
      const title = document.createElement("p");
      title.className = "ws-card-title ws-hestan-winetitle";
      title.title = wine.title;
      title.textContent = wine.title;
      body.appendChild(title);
    }

    const meta = metaLine(wine);
    if (meta) {
      const desc = document.createElement("p");
      desc.className = "ws-hestan-winecard-meta";
      desc.textContent = meta;
      body.appendChild(desc);
    }

    // Bottom row: [price + size on the left]  [BUY NOW on the right]
    const bottom = document.createElement("div");
    bottom.className = "ws-hestan-bottom";

    const priceBlock = document.createElement("div");
    priceBlock.className = "ws-hestan-priceblock";
    if (wine.regular_price) {
      const price = document.createElement("p");
      price.className = "ws-card-price";
      price.textContent = wine.regular_price;
      priceBlock.appendChild(price);
    }
    // Old createWineListing product-footer: price + bottle_quantity stacked.
    if (wine.bottle_quantity) {
      const qty = document.createElement("p");
      qty.className = "ws-card-unit ws-hestan-bottle-qty";
      qty.textContent = wine.bottle_quantity;
      priceBlock.appendChild(qty);
    } else if (wine.price_unit) {
      const unit = document.createElement("p");
      unit.className = "ws-card-unit";
      unit.textContent = wine.price_unit;
      priceBlock.appendChild(unit);
    }
    if (priceBlock.children.length) bottom.appendChild(priceBlock);

    // External "Add to cart" / "BUY NOW" link (hestan has no in-widget cart).
    if (wine.cta_link && !soldOut && !unavailable) {
      const cta = document.createElement("a");
      cta.className = "ws-card-cta ws-hestan-cta";
      cta.href = wine.cta_link;
      cta.target = "_blank";
      cta.rel = "noopener noreferrer";
      cta.textContent = wine.cta_text || "BUY NOW";
      bottom.appendChild(cta);
    }

    if (bottom.children.length) body.appendChild(bottom);

    card.appendChild(body);

    // Sold-out: whole card → detail view (old createWineListing wrapped the
    // .card in an onclick on the sold-out branch).
    if (soldOut) {
      card.style.cursor = "pointer";
      card.addEventListener("click", (e) => {
        if (e.target.closest("a, button")) return;
        openDetail();
      });
    }

    return card;
  }
}
